import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { 
    Send, Paperclip, MessageCircle, Mail, Bot, 
    Sparkles, AlertCircle, CheckCircle, 
    Clock, ShieldAlert, X, Zap, ChevronDown,
    User, ArrowLeftRight, UserCheck, FileText,
    Shield, ShieldCheck, Smartphone, RefreshCw
} from 'lucide-react';

// --- CONFIGURATION: REASON CODES ---
const ACTION_REASONS = {
    Hold: [
        { id: 'sys_trade', label: 'System Issue - Trading' },
        { id: 'sys_bank', label: 'System Issue - Banking' },
        { id: 'sys_edu', label: 'System Issue - Education' },
        { id: 'pay_proc', label: 'Payment Processing Issue' }
    ],
    Escalate: [
        { id: 'goodwill', label: 'Goodwill / Compensation' },
        { id: 'invest', label: 'Investigation Required' },
        { id: 'sec_login', label: 'Login / Security Escalation' }
    ],
    Close: [
        { id: 'resolved', label: 'Issue Resolved' },
        { id: 'cust_confirm', label: 'Customer Confirmed' },
        { id: 'no_resp', label: 'No Response (Manual Close)' },
        { id: 'duplicate', label: 'Duplicate Ticket' }
    ]
};

// --- CONFIGURATION: EMAIL TEMPLATES ---
const EMAIL_TEMPLATES = [
    { id: 'welcome', label: 'Welcome / Onboarding', body: "Dear {{client_name}},\n\nWelcome to Nommia! We are thrilled to have you with us. Please let us know if there is anything we can help you with to get started.\n\nBest regards,\nNommia Support Team" },
    { id: 'kyc_req', label: 'KYC Documents Missing', body: "Dear {{client_name}},\n\nThank you for your cooperation. To proceed with your request, we kindly ask that you provide a valid Proof of Identity and Proof of Address.\n\nYou can upload these directly via your client portal.\n\nRegards,\nCompliance Team" },
    { id: 'dep_delay', label: 'Deposit Delay Explanation', body: "Dear {{client_name}},\n\nWe are currently experiencing a slight delay with our payment processor. Your funds are safe and should reflect in your account within 2-4 hours.\n\nWe apologize for the inconvenience.\n\nBest,\nSupport" },
    { id: 'ticket_autoclose', label: 'Ticket Auto-Close Notice', body: "Dear {{client_name}},\n\nWe noticed you haven't replied to this ticket in over 14 days. We are now closing this thread. If you still need help, please open a new ticket.\n\nBest regards,\nNommia Support" }
];

// --- CONFIGURATION: STAFF MEMBERS ---
const AVAILABLE_AGENTS = [
    { id: 'me', name: 'Me (Current User)' },
    { id: 'sarah', name: 'Sarah Jenkins (Snr Agent)' },
    { id: 'mike', name: 'Mike Ross (Compliance)' },
    { id: 'queue', name: 'Unassigned Queue' }
];

const MOCK_TICKET = {
    id: "TKT-10024",
    client: { name: "Alex Thompson", id: "882910", phone: "+44 7700 900077" },
    status: "New", 
    subject: "Deposit not showing in MT5",
    assignedTo: 'queue', 
    history: [
        { id: 1, type: 'email', sender: 'client', text: "Hi, I deposited $5000 via Crypto 2 hours ago but my account is still empty.", timestamp: "10:30 AM" },
        { id: 2, type: 'system', sender: 'auto', text: "Ticket #TKT-10024 generated. Our team will review shortly.", timestamp: "10:30 AM" }
    ],
    aiContext: {
        sentiment: "Frustrated",
        intent: "Payment_Issue",
        drafts: [
            { id: 1, type: "Empathy", text: "I understand your frustration, Alex. Let me check the blockchain transaction for you immediately." },
            { id: 2, type: "Policy", text: "Crypto deposits typically take 1-3 network confirmations. Could you please provide the TXID?" }
        ],
        liveNotes: [
            "Client identified as VIP (Tier 1)",
            "Issue relates to Crypto/Banking"
        ]
    }
};

const TicketWorkspace = ({ ticket, onBack, isEmbedded, onActivity, currentUserRole = 'Manager', currentClient }) => {

    const navigate = useNavigate();
    console.log("🔍 INCOMING TICKET DATA:", ticket);

    // --- NEW: LIVE CLIENT OVERRIDE STATE ---
    const [linkedClient, setLinkedClient] = useState(null);

    // --- SAFE DATA MERGING ---
    // We override the default MOCK_TICKET.client with 'currentClient' (James Sterling)
    const activeTicket = ticket ? {
        ...MOCK_TICKET,
        ...ticket,
        client: {
            ...MOCK_TICKET.client,      // 1. Start with defaults (Alex)
            ...(currentClient || {}),    // 2. OVERRIDE with real profile data (James)
            ...(ticket.client || {}),    // 3. If ticket specifically has client data, use that
            ...(linkedClient || {})      // 4. THE FIX: Instantly override UI when linked!
        }
    } : { ...MOCK_TICKET, client: linkedClient || currentClient || MOCK_TICKET.client };
    
const activeLiveChatId = activeTicket.live_chat_id || activeTicket.liveChatId;

    const [status, setStatus] = useState(activeTicket.status);
    const [owner, setOwner] = useState(activeTicket.assignedTo || 'queue'); 
    const [ownerName, setOwnerName] = useState('Loading...');
    const [inputText, setInputText] = useState("");
    const [messages, setMessages] = useState(activeTicket.history || []);
    const [replyChannel, setReplyChannel] = useState(activeLiveChatId ? 'live_chat' : 'email');
    
    // --- NEW: AUTO-SCROLL LOGIC ---
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]); // Triggers every time the 'messages' array updates

    // --- NEW: FETCH OWNER NAME ---
    useEffect(() => {
        const fetchOwnerName = async () => {
            if (owner === 'queue') {
                setOwnerName('Unassigned Queue');
                return;
            }
            
            // Fallback for mock IDs
            const hardcodedAgent = AVAILABLE_AGENTS.find(a => a.id === owner);
            if (hardcodedAgent) {
                setOwnerName(hardcodedAgent.name);
                return;
            }

            // Fetch real profile from Supabase
            if (owner && owner.length > 20) {
                try {
                    const { data, error } = await supabase
                        .from('profiles')
                        .select('display_name, full_name')
                        .eq('id', owner)
                        .single();
                    
                    if (data) {
                        setOwnerName(data.display_name || data.full_name || 'Support Agent');
                    } else {
                        setOwnerName('Unknown Agent');
                    }
                } catch (err) {
                    console.error("Failed to fetch owner name:", err);
                    setOwnerName('Unknown Agent');
                }
            } else {
                setOwnerName(owner);
            }
        };
        fetchOwnerName();
    }, [owner]);

    // --- NEW: TYPING INDICATOR STATE & REFS ---
    const [isClientTyping, setIsClientTyping] = useState(false);
    const typingTimeoutRef = useRef(null);
    const chatChannelRef = useRef(null); // Holds the connection so the agent can broadcast back

    // --- TEMPLATE STATE ---
    const [showTemplates, setShowTemplates] = useState(false);

    // --- SECURITY / OTP STATE ---
    const [isVerified, setIsVerified] = useState(false);
    const [otpModal, setOtpModal] = useState({ show: false, step: 'send', code: '', input: '' });

    // --- MODAL STATE ---
    const [activeAction, setActiveAction] = useState(null); 
    const [actionData, setActionData] = useState({ reason: '', note: '', targetAgent: '', targetClient: '' });
    const [clientSearchQuery, setClientSearchQuery] = useState('');
    const [clientSearchResults, setClientSearchResults] = useState([]);
    const [isSearchingClients, setIsSearchingClients] = useState(false);
    const [selectedClientPreview, setSelectedClientPreview] = useState(null);

    // --- NEW: AI DRAFT STATE & GENERATION ---
    const [aiDrafts, setAiDrafts] = useState([]);
    const [isGeneratingDrafts, setIsGeneratingDrafts] = useState(false);
    const [isSending, setIsSending] = useState(false);

    // --- NEW: INTERNAL NOTES STATE ---
    const [newInternalNote, setNewInternalNote] = useState('');

    const handleAddInternalNote = async () => {
        if (!newInternalNote.trim()) return;

        const timestamp = new Date().toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        const noteMsg = {
            id: Date.now(),
            type: 'internal_note',
            sender: currentUserRole || 'Agent',
            text: newInternalNote.trim(),
            timestamp: timestamp
        };

        const updatedHistory = [...messages, noteMsg];
        setMessages(updatedHistory);
        setNewInternalNote('');

        try {
            const { error } = await supabase
                .from('crm_tickets')
                .update({ history: updatedHistory })
                .eq('id', activeTicket.fullId || activeTicket.id);
                
            if (error) throw error;
        } catch (err) {
            console.error("Failed to save internal note:", err);
        }
    };

    useEffect(() => {
        const generateDrafts = async () => {
            // THE FIX: Filter out system messages and ignore the dummy placeholder
            const validClientMessages = messages.filter(m => 
                m.sender?.toLowerCase() !== 'system' && 
                m.sender?.toLowerCase() !== 'auto' && 
                m.sender?.toLowerCase() !== 'agent' &&
                m.sender?.toLowerCase() !== 'me' &&
                m.text && 
                !m.text.includes('No description provided.')
            );
            
            // Grab the MOST RECENT message the client sent
            const latestClientMessage = validClientMessages.length > 0 
                ? validClientMessages[validClientMessages.length - 1].text 
                : null;

            if (!latestClientMessage) return;

            setIsGeneratingDrafts(true);
            try {
                const { data, error } = await supabase.functions.invoke('generate-ticket-drafts', {
                    body: { 
                        ticket_content: latestClientMessage,
                        client_name: activeTicket.client?.name || 'Client'
                    }
                });

                if (error) throw error;
                
                // Map the raw strings from Gemini into your UI structure
                if (data && data.drafts) {
                    const formattedDrafts = [
                        { id: 1, type: "Direct & Solution", text: data.drafts[0] },
                        { id: 2, type: "Apologetic & Reassuring", text: data.drafts[1] },
                        { id: 3, type: "Clarification Needed", text: data.drafts[2] }
                    ];
                    setAiDrafts(formattedDrafts);
                }
            } catch (err) {
                console.error("Failed to generate AI drafts:", err);
            } finally {
                setIsGeneratingDrafts(false);
            }
        };

        // Only run if we haven't already generated drafts for this ticket
        if (aiDrafts.length === 0) {
            generateDrafts();
        }
    }, [messages, activeTicket]);

    // --- NEW: LIVE CHAT WEBSOCKET LISTENER ---
    useEffect(() => {
        // Only subscribe if we are actually in a Live Chat ticket
        if (replyChannel !== 'live_chat' || !activeTicket.live_chat_id) return;

        console.log("Subscribing to Live Chat ID:", activeTicket.live_chat_id);

        // 1. ADDED TASK: Fetch the historical transcript the moment the room opens!
        const fetchInitialHistory = async () => {
            try {
                const { data, error } = await supabase
                    .from('crm_live_chats')
                    .select('transcript')
                    .eq('id', activeTicket.live_chat_id)
                    .single();
                
                if (data && data.transcript && data.transcript.length > 0) {
                    setMessages(data.transcript);
                }
            } catch (err) {
                console.error("Failed to load initial chat history:", err);
            }
        };
        
        fetchInitialHistory();

        // 2. EXISTING TASK: Start listening for new messages coming in
        chatChannelRef.current = supabase.channel(`live-chat-${activeTicket.live_chat_id}`);
        
        chatChannelRef.current
            .on(
                'postgres_changes',
                {
                    event: 'UPDATE',
                    schema: 'public',
                    table: 'crm_live_chats',
                    filter: `id=eq.${activeTicket.live_chat_id}`
                },
                (payload) => {
                    console.log("Live Chat Update Received:", payload);
                    if (payload.new && payload.new.transcript) {
                        setMessages(payload.new.transcript);
                        setIsClientTyping(false); // Hide typing bubble the moment a message arrives!
                    }
                }
            )
            // --- NEW: Listen for the Client's invisible typing pings ---
            .on(
                'broadcast',
                { event: 'typing' },
                (payload) => {
                    if (payload.payload?.sender === 'client') {
                        setIsClientTyping(true);
                        
                        // Auto-hide the bubble after 3 seconds of silence
                        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
                        typingTimeoutRef.current = setTimeout(() => setIsClientTyping(false), 3000);
                    }
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(chatChannelRef.current);
            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        };
    }, [replyChannel, activeTicket.live_chat_id]);
    // --- AUTOMATION: STATUS CHANGE ON READ ---
    useEffect(() => {
        if (status === 'New') {
            const timer = setTimeout(() => setStatus('Open'), 1000); 
            return () => clearTimeout(timer);
        }
    }, [status]);

// --- HANDLER: SEND MESSAGE (UPDATED FOR LIVE CHAT & EMAIL) ---
    const handleSend = async () => {
        // THE FIX: Prevent firing if empty or already sending!
        if (!inputText.trim() || isSending) return; 
        
        setIsSending(true); // Lock the button

        if (owner === 'queue') setOwner('me');

        // 1. Instantly update the UI so the agent sees their message locally
        const newMessage = { 
            id: Date.now(), 
            type: replyChannel, 
            sender: currentUserRole || 'Agent', 
            text: inputText, 
            timestamp: new Date().toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) 
        };
        
        const updatedTranscript = [...messages, newMessage];
        setMessages(updatedTranscript);
        
        const targetId = activeTicket.fullId || activeTicket.id;
        
        // 2. LIVE EMAIL & LIVE CHAT PIPELINES
        if (replyChannel === 'email') {
            try {
                const { data: { session } } = await supabase.auth.getSession();

                const { error: emailError } = await supabase.functions.invoke('send-branded-email', {
                    body: {
                        customMessage: inputText, 
                        agentName: currentUserRole || 'Support Agent', 
                        clientData: {
                            email: activeTicket.client?.email || activeTicket.email || activeTicket.client_email,     
                            firstName: activeTicket.client?.name?.split(' ')[0] || 'Client',
                            lastName: activeTicket.client?.name?.split(' ')[1] || '',
                            id: activeTicket.client?.id,
                            tier: activeTicket.client?.tier
                        },
                        ticketData: {
                            id: activeTicket.id
                        },
                        emailSubject: activeTicket.subject 
                    },
                    headers: {
                        Authorization: `Bearer ${session?.access_token}`
                    }
                });

                if (emailError) throw emailError;

                // --- SAVE AGENT REPLY TO THE SUPABASE DATABASE ---
                const { data: updateData, error: dbError } = await supabase
                    .from('crm_tickets')
                    .update({ 
                        status: 'Awaiting Client Reply',
                        history: updatedTranscript 
                    })
                    .eq('id', targetId)
                    .select();

                if (dbError) console.error("[DB Update] Database save failed:", dbError);
                
                setStatus('Awaiting Client Reply'); 
                
            } catch (err) {
                console.error("Failed to send email via Power Automate:", err);
                alert("Warning: The reply was saved to the ticket locally, but the email failed to send.");
            }
        } else if (replyChannel === 'live_chat') {
            // --- NEW: LIVE CHAT PIPELINE ---
            try {
                if (!activeTicket.live_chat_id) {
                    throw new Error("No active Live Chat connection found for this ticket.");
                }

                // Push the message to the Live Chat table (Triggers WebSocket broadcast to Portal)
                const { error: chatError } = await supabase
                    .from('crm_live_chats')
                    .update({ 
                        transcript: updatedTranscript,
                        status: 'Live_Agent_Connected', // Updates status so Portal knows agent is active
                        updated_at: new Date().toISOString()
                    })
                    .eq('id', activeLiveChatId);

                if (chatError) throw chatError;

                // Sync the permanent CRM ticket history as well
                await supabase
                    .from('crm_tickets')
                    .update({ history: updatedTranscript })
                    .eq('id', targetId);

                if (status === 'New' || status === 'Open') setStatus('Ongoing');

            } catch (err) {
                console.error("Live Chat Send Error:", err);
                alert(`Live Chat Error: ${err.message}`);
            }
        } else {
            if (status === 'Open' || status === 'New') setStatus('Ongoing');
        }

        setInputText("");

        // 3. THE FIX: Sync to Parent with the FULL Transcript and Ticket ID!
        if (onActivity) {
            onActivity('message', {
                channel: replyChannel,
                ticketSubject: activeTicket.subject,
                text: inputText,
                ticketId: targetId, 
                transcript: updatedTranscript 
            });
        }

        setIsSending(false); // Unlock the button
    };

    // --- HANDLER: REOPEN TICKET ---
    const handleReopenTicket = async () => {
        const systemNote = {
            id: Date.now(),
            type: 'system',
            sender: 'system',
            text: `Ticket reopened by ${currentUserRole}.`,
            timestamp: new Date().toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
        };
        
        const updatedTranscript = [...messages, systemNote];
        
        setStatus('Open');
        setMessages(updatedTranscript);

        try {
            await supabase
                .from('crm_tickets')
                .update({ status: 'Open', history: updatedTranscript })
                .eq('id', activeTicket.fullId || activeTicket.id);
        } catch (err) {
            console.error("Error reopening ticket:", err);
        }

        if (onActivity) {
            onActivity('status', {
                ticketId: activeTicket.id,
                ticketSubject: activeTicket.subject,
                action: 'Reopened',
                status: 'Open', // 🚨 THE FIX: Explicitly tell the parent it is Open!
                reason: 'Manual Reopen',
                transcript: updatedTranscript,
                client: activeTicket.client
            });
        }
    };

    // --- HANDLER: CLAIM TICKET ---
    const handleClaim = () => {
        if (!window.confirm("Are you sure you want to take over this ticket?")) return;
        setOwner('me');
        const systemNote = {
            id: Date.now(),
            type: 'system',
            sender: 'system',
            text: `Ownership claimed by ${currentUserRole}. Previous owner: ${AVAILABLE_AGENTS.find(a => a.id === owner)?.name || 'Unknown'}`,
            timestamp: new Date().toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
        };
        setMessages([...messages, systemNote]);
        if (onActivity) onActivity('status', { ticketId: activeTicket.id, action: 'Claimed', reason: 'Self-Assignment' });
    };

    // --- HANDLER: OTP SECURITY ---
    const handleSendOtp = async () => {
        console.log("🚨 SEND OTP BUTTON WAS CLICKED!");
        
        const clientPhone = activeTicket.client?.phone;
        console.log("📞 Target Phone Number:", clientPhone);
        
        // ... rest of the code
        if (!clientPhone || clientPhone === 'N/A' || clientPhone === 'Unknown') {
            return alert("Cannot verify: No valid phone number found for this client.");
        }

        // 1. Generate Code and UI State
        const randomCode = Math.floor(1000 + Math.random() * 9000).toString();
        setOtpModal({ ...otpModal, step: 'verify', code: randomCode, input: '' });

        // 2. Actually Fire the WhatsApp API!
        try {
            const response = await fetch(`${process.env.REACT_APP_NOMMIA_API_URL}/api/wa/send-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    phone: clientPhone, 
                    code: randomCode, 
                    template: 'verification_template' 
                })
            });

            const data = await response.json();
            if (!data.status) throw new Error("WhatsApp Delivery Failed");

            const systemLog = {
                id: Date.now(),
                type: 'system',
                sender: 'system',
                text: `[SYSTEM] OTP Code generated and dispatched to ${clientPhone} via WhatsApp.`,
                timestamp: new Date().toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
            };
            setMessages(prev => [...prev, systemLog]);

        } catch (error) {
            console.error("OTP Delivery Error:", error);
            alert("The OTP modal is open, but the WhatsApp message failed to send to the client's phone.");
        }
    };

    const handleVerifyOtp = () => {
        if (otpModal.input === otpModal.code) {
            // Success
            setIsVerified(true);
            setOtpModal({ show: false, step: 'send', code: '', input: '' });
            
            // Log Success
            const successLog = {
                id: Date.now(),
                type: 'system',
                sender: 'system',
                text: `✅ Identity Verified. Agent confirmed OTP match.`,
                timestamp: new Date().toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
            };
            setMessages(prev => [...prev, successLog]);
        } else {
            // Failure
            alert("Incorrect Code. verification failed.");
        }
    };

    // --- NEW: LIVE SEARCH NOMMIA API CLIENTS ---
    const handleSearchClients = async (searchTerm) => {
        setClientSearchQuery(searchTerm);
        
        // Clear results if the input is empty or too short
        if (!searchTerm || searchTerm.length < 2) {
            setClientSearchResults([]);
            return;
        }

        setIsSearchingClients(true);
        try {
            // 1. Fetch from the Nommia external API
            const response = await fetch('https://api-trade.nommia.io/client-api/users?page=1&limit=100', {
                method: 'GET',
                headers: {
                    'x-client-api-key': '65f84ac479c889266b545881cf8abd7c3e1ff7958c08789c8d08e749da034cc1'
                }
            });

            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }

            const data = await response.json();
            const lowerTerm = searchTerm.toLowerCase();

            // 2. Filter the returned API users locally by name or email
            const matchedUsers = (data.users || []).filter(user => {
                // 🚨 FIX: Use 'user.name' as defined in the Nommia API spec
                const fullName = (user.name || '').toLowerCase();
                const email = (user.email || '').toLowerCase();
                return fullName.includes(lowerTerm) || email.includes(lowerTerm);
            });

            // 3. Map the Nommia fields to our UI format
            const formattedResults = matchedUsers.slice(0, 5).map(user => ({
                id: user.accountId || user.userId, // Safely handle varying ID keys
                name: user.name || 'Unknown Client',
                email: user.email
            }));

            setClientSearchResults(formattedResults);
        } catch (err) {
            console.error("Error searching Nommia API clients:", err);
            setClientSearchResults([]); // Ensure UI clears on error
        } finally {
            setIsSearchingClients(false);
        }
    };

    // --- HANDLER: SUBMIT MODAL ACTION ---
    const handleSubmitAction = async () => {
        // Validation checks
        if (activeAction === 'LinkClient' && !actionData.targetClient) return alert("Please enter a Client ID or Email.");
        if (activeAction === 'Reassign' && !actionData.targetAgent) return alert("Please select an agent.");
        if (activeAction !== 'Reassign' && activeAction !== 'LinkClient' && !actionData.reason) return alert("Please select a reason.");
        
        let logText = '';
        let logAction = '';
        const targetId = activeTicket.fullId || activeTicket.id;

        // NEW LOGIC: Handling the Profile Link
        if (activeAction === 'LinkClient') {
            const { error } = await supabase
                .from('crm_tickets')
                .update({ 
                    client_id: actionData.targetClient,
                    client_email: selectedClientPreview?.email || null,
                    // THE FIX: Cache the full profile object so the Dashboard widget can read the name!
                    client: {
                        id: actionData.targetClient,
                        name: selectedClientPreview?.name || actionData.targetClient,
                        email: selectedClientPreview?.email || null
                    }
                })
                .eq('id', targetId);

            if (error) {
                console.error("Error linking client:", error);
                return alert("Failed to link client to ticket.");
            }

            logText = `Ticket linked to verified profile: ${selectedClientPreview?.name || actionData.targetClient}`;
            logAction = 'Profile Linked';
            actionData.note = actionData.note || "System auto-note: Account merged.";
            
            // THE FIX: Instantly update the UI with the searched client's data!
            if (selectedClientPreview) {
                setLinkedClient(selectedClientPreview);
            }
            
            // --- NEW: SEAMLESS REDIRECT ---
            // Teleport the agent to the real profile page, replacing the 'unknown' URL in history
            navigate(`/client/${actionData.targetClient}`, {
                state: { activeTab: 'tickets', ticketId: targetId },
                replace: true
            });
            
        } else if (activeAction === 'Reassign') {
            // EXISTING LOGIC: Reassignment
            setOwner(actionData.targetAgent);
            const newOwnerName = AVAILABLE_AGENTS.find(a => a.id === actionData.targetAgent)?.name;
            logText = `Ticket reassigned to ${newOwnerName}. Note: ${actionData.note}`;
            logAction = 'Reassigned';
            
        } else {
            // EXISTING LOGIC: Status Changes (Hold/Escalate/Close)
            setStatus(activeAction === 'Hold' ? 'Hold' : activeAction === 'Escalate' ? 'Escalated' : 'Closed');
            const reasonLabel = ACTION_REASONS[activeAction].find(r => r.id === actionData.reason)?.label;
            logText = `Ticket ${activeAction === 'Close' ? 'Closed' : activeAction}. Reason: ${reasonLabel}. Note: ${actionData.note}`;
            logAction = activeAction === 'Close' ? 'Closed' : activeAction;
        }

        // 1. Generate the system note FIRST
        const systemNote = {
            id: Date.now(),
            type: 'system',
            sender: 'system',
            text: logText,
            timestamp: new Date().toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
        };
        
        const updatedTranscript = [...messages, systemNote];

        // 🚨 2. THE FIX: Push the Status AND the History to Supabase!
        try {
            // A. Always update the main ticket table with the new status
            if (['Hold', 'Escalate', 'Close'].includes(activeAction)) {
                const newDbStatus = activeAction === 'Close' ? 'Closed' : activeAction === 'Escalate' ? 'Escalated' : 'Hold';
                await supabase
                    .from('crm_tickets')
                    .update({ 
                        status: newDbStatus, 
                        history: updatedTranscript 
                    })
                    .eq('id', activeTicket.fullId || activeTicket.id);
            } else if (activeAction === 'Reassign') {
                await supabase
                    .from('crm_tickets')
                    .update({ 
                        assigned_to: actionData.targetAgent,
                        history: updatedTranscript 
                    })
                    .eq('id', activeTicket.fullId || activeTicket.id);
            } else if (activeAction === 'LinkClient') {
                 await supabase
                    .from('crm_tickets')
                    .update({ history: updatedTranscript })
                    .eq('id', activeTicket.fullId || activeTicket.id);
            }

            // B. If it's a close action and linked to a live chat, close the client's portal too
            if (activeAction === 'Close' && activeTicket.live_chat_id) {
                await supabase
                    .from('crm_live_chats')
                    .update({ 
                        status: 'Closed', 
                        transcript: updatedTranscript,
                        updated_at: new Date().toISOString() 
                    })
                    .eq('id', activeTicket.live_chat_id);
            }
        } catch (dbErr) {
            console.error("Failed to sync ticket update to DB:", dbErr);
        }

        // 3. Update local UI

        // 3. Update local UI
        setMessages(updatedTranscript);

        // 3. Sync to Parent (Updated to include STRICT STATUS)
        if (onActivity) {
            onActivity('status', {
                ticketId: activeTicket.id, 
                ticketSubject: activeTicket.subject, 
                action: logAction, 
                // 🚨 THE FIX: Explicitly tell the parent exactly what the new status is!
                status: activeAction === 'Close' ? 'Closed' : activeAction === 'Hold' ? 'Hold' : activeAction === 'Escalate' ? 'Escalated' : 'Open',
                reason: activeAction === 'Reassign' ? `To: ${actionData.targetAgent}` : activeAction === 'LinkClient' ? actionData.targetClient : actionData.reason,
                transcript: messages,
                client: activeAction === 'LinkClient' ? {
                    id: actionData.targetClient,
                    name: selectedClientPreview?.name || actionData.targetClient,
                    email: selectedClientPreview?.email || null
                } : activeTicket.client
            });
        }
        
        // Reset state, making sure to clear the new targetClient field too!
        setActiveAction(null);
        setActionData({ reason: '', note: '', targetAgent: '', targetClient: '' });
        setClientSearchQuery(''); 
        setSelectedClientPreview(null);
    };

    // --- COMPONENT: STATUS BADGE ---
    const StatusBadge = ({ current }) => {
        const colors = {
            New: "bg-blue-500/10 text-blue-400 border-blue-500/20",
            Open: "bg-amber-500/10 text-amber-400 border-amber-500/20",
            Ongoing: "bg-green-500/10 text-green-400 border-green-500/20",
            Hold: "bg-purple-500/10 text-purple-400 border-purple-500/20",
            Escalated: "bg-red-500/10 text-red-400 border-red-500/20",
            Closed: "bg-slate-800 text-slate-500 border-slate-700"
        };
        return (
            <span className={`px-3 py-1 rounded-full text-xs font-bold border flex items-center gap-2 ${colors[current] || colors['New']}`}>
                <div className={`w-1.5 h-1.5 rounded-full ${current === 'Ongoing' ? 'bg-green-500 animate-pulse' : 'bg-current'}`}></div>
                {current ? current.toUpperCase() : 'UNKNOWN'}
            </span>
        );
    };

    return (
        <div className={`flex flex-col h-full bg-slate-900 overflow-hidden animate-fade-in ${isEmbedded ? 'rounded-none border-0' : 'rounded-2xl border border-slate-800'}`}>
            
            {/* --- OTP SECURITY MODAL --- */}
            {otpModal.show && (
                <div className="absolute inset-0 z-[60] bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-sm shadow-2xl p-6 text-center">
                        <div className="w-12 h-12 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center mx-auto mb-4">
                            <Smartphone size={24} />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Security Verification</h3>
                        {/* CRITICAL FIX: Safe access for phone number below */}
                        <p className="text-sm text-slate-400 mb-6">
                            {otpModal.step === 'send' 
                                ? `Send a One-Time Password to the client's registered WhatsApp (${activeTicket.client?.phone || 'No Phone'})?` 
                                : `Enter the 4-digit code the client received.`}
                        </p>

                        {otpModal.step === 'send' ? (
                            <div className="flex gap-3">
                                <button onClick={() => setOtpModal({...otpModal, show: false})} className="flex-1 py-3 bg-slate-800 text-slate-300 font-bold rounded-lg hover:bg-slate-700">Cancel</button>
                                <button onClick={handleSendOtp} className="flex-1 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-500 flex items-center justify-center gap-2">
                                    <Send size={16} /> Send Code
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <input 
                                    className="w-full bg-slate-950 border border-slate-700 rounded-xl p-4 text-center text-2xl tracking-[0.5em] font-mono text-white focus:border-blue-500 outline-none"
                                    maxLength={4}
                                    placeholder="0000"
                                    value={otpModal.input}
                                    onChange={(e) => setOtpModal({...otpModal, input: e.target.value.replace(/\D/g,'')})}
                                    autoFocus
                                />
                                <button onClick={handleVerifyOtp} className="w-full py-3 bg-green-600 text-white font-bold rounded-lg hover:bg-green-500 flex items-center justify-center gap-2">
                                    <ShieldCheck size={16} /> Verify Identity
                                </button>
                                <button onClick={() => setOtpModal({ show: false, step: 'send', code: '', input: '' })} className="text-xs text-slate-500 hover:text-white">Cancel Verification</button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* --- ACTION MODAL --- */}
            {activeAction && (
                <div className="absolute inset-0 z-50 bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-sm shadow-2xl p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className={`text-xl font-bold flex items-center gap-2 ${
                                activeAction === 'Hold' ? 'text-purple-400' : 
                                activeAction === 'Escalate' ? 'text-red-400' : 
                                activeAction === 'Close' ? 'text-green-400' : 'text-blue-400'
                            }`}>
                                {activeAction === 'Hold' ? <Clock size={20}/> : 
                                 activeAction === 'Escalate' ? <ShieldAlert size={20}/> : 
                                 activeAction === 'Close' ? <CheckCircle size={20}/> : <ArrowLeftRight size={20}/>}
                                
                                {activeAction === 'Reassign' ? 'Transfer Ownership' : 
                                 activeAction === 'Hold' ? 'Put Ticket on Hold' : 
                                 activeAction === 'Close' ? 'Resolve & Close' : 'Escalate Ticket'}
                            </h3>
                            <button onClick={() => setActiveAction(null)} className="text-slate-400 hover:text-white"><X size={20}/></button>
                        </div>
                        
                        <div className="space-y-4">
                            {activeAction === 'LinkClient' ? (
                                <div className="relative">
                                    <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Search CRM Profiles</label>
                                    
                                    {/* Show Search Input if no client is selected yet */}
                                    {!selectedClientPreview ? (
                                        <>
                                            <div className="relative">
                                                <input 
                                                    type="text"
                                                    className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white outline-none focus:border-blue-500"
                                                    placeholder="Search name or email..."
                                                    value={clientSearchQuery}
                                                    onChange={(e) => handleSearchClients(e.target.value)}
                                                    autoFocus
                                                />
                                                {isSearchingClients && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>}
                                            </div>

                                            {/* Search Results Dropdown */}
                                            {clientSearchResults.length > 0 && (
                                                <div className="absolute z-10 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                                                    {clientSearchResults.map(client => (
                                                        <div 
                                                            key={client.id}
                                                            onClick={() => {
                                                                setActionData({...actionData, targetClient: client.id});
                                                                setSelectedClientPreview(client);
                                                                setClientSearchResults([]); // Hide dropdown
                                                            }}
                                                            className="p-3 border-b border-slate-700/50 hover:bg-slate-700 cursor-pointer transition-colors"
                                                        >
                                                            <p className="text-sm font-bold text-white">{client.name}</p>
                                                            <p className="text-xs text-slate-400">{client.email || 'No email'} • ID: {client.id.substring(0,8)}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {clientSearchQuery.length >= 2 && clientSearchResults.length === 0 && !isSearchingClients && (
                                                <p className="text-xs text-slate-500 mt-2">No clients found matching "{clientSearchQuery}"</p>
                                            )}
                                        </>
                                    ) : (
                                        /* Show Selected Client Card */
                                        <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-xl flex justify-between items-center">
                                            <div>
                                                <p className="text-[10px] text-blue-400 font-bold uppercase mb-1 flex items-center gap-1"><CheckCircle size={10}/> Selected Profile</p>
                                                <p className="text-sm font-bold text-white">{selectedClientPreview.name}</p>
                                                <p className="text-xs text-slate-400">{selectedClientPreview.email}</p>
                                            </div>
                                            <button 
                                                onClick={() => {
                                                    setSelectedClientPreview(null);
                                                    setActionData({...actionData, targetClient: ''});
                                                }}
                                                className="text-slate-400 hover:text-red-400 transition-colors p-2"
                                                title="Remove selection"
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : activeAction === 'Reassign' ? (
                                <div>
                                    <label className="text-xs font-bold text-slate-400 uppercase block mb-2">New Owner</label>
                                    <div className="relative">
                                        <select 
                                            className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white appearance-none outline-none focus:border-blue-500"
                                            value={actionData.targetAgent}
                                            onChange={(e) => setActionData({...actionData, targetAgent: e.target.value})}
                                        >
                                            <option value="">Select an agent...</option>
                                            {AVAILABLE_AGENTS.map(agent => (
                                                <option key={agent.id} value={agent.id}>{agent.name}</option>
                                            ))}
                                        </select>
                                        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"/>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <label className="text-xs font-bold text-slate-400 uppercase block mb-2">
                                        {activeAction === 'Close' ? 'Resolution Code' : 'Reason Code'}
                                    </label>
                                    <div className="relative">
                                        <select 
                                            className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white appearance-none outline-none focus:border-blue-500"
                                            value={actionData.reason}
                                            onChange={(e) => setActionData({...actionData, reason: e.target.value})}
                                        >
                                            <option value="">Select a category...</option>
                                            {ACTION_REASONS[activeAction]?.map(r => (
                                                <option key={r.id} value={r.id}>{r.label}</option>
                                            ))}
                                        </select>
                                        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"/>
                                    </div>
                                </div>
                            )}
                            
                            <div>
                                <label className="text-xs font-bold text-slate-400 uppercase block mb-2">
                                    {activeAction === 'Close' ? 'Resolution Note (Required)' : 'Internal Note (Required)'}
                                </label>
                                <textarea 
                                    className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white text-sm min-h-[100px] outline-none focus:border-blue-500 resize-none"
                                    placeholder={activeAction === 'Reassign' ? "Reason for transfer..." : "Add context..."}
                                    value={actionData.note}
                                    onChange={(e) => setActionData({...actionData, note: e.target.value})}
                                />
                            </div>

                            <div className="pt-2 flex gap-3">
                                <button onClick={() => setActiveAction(null)} className="flex-1 py-3 bg-slate-800 text-slate-300 font-bold rounded-lg hover:bg-slate-700">Cancel</button>
                                <button 
                                    onClick={handleSubmitAction}
                                    className={`flex-1 py-3 font-bold rounded-lg text-slate-900 transition-colors ${
                                        activeAction === 'Hold' ? 'bg-purple-500 hover:bg-purple-400' : 
                                        activeAction === 'Escalate' ? 'bg-red-500 hover:bg-red-400' : 
                                        activeAction === 'Close' ? 'bg-green-500 hover:bg-green-400' : 'bg-blue-500 hover:bg-blue-400'
                                    }`}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 1. HEADER BAR */}
            <div className="h-16 border-b border-slate-800 flex items-center justify-between px-6 bg-slate-900 flex-shrink-0">
                <div className="flex items-center gap-4">
                    <button onClick={onBack} className="text-slate-400 hover:text-white transition-colors">
                        <X size={20} />
                    </button>
                    <div>
                        <div className="flex items-center gap-3">
                            <h2 className="text-white font-bold text-lg">
                                {activeTicket.client?.name || "Current Client"}
                            </h2>
                            
                            {/* --- NEW: LINK CLIENT BUTTON --- */}
                            {(!activeTicket.client?.id || activeTicket.client?.name === 'Unknown Sender') && (
                                <button 
                                    onClick={() => setActiveAction('LinkClient')}
                                    className="bg-blue-600 hover:bg-blue-500 text-white text-[10px] px-2 py-1 rounded font-bold uppercase transition-colors shadow-sm"
                                >
                                    Link Profile
                                </button>
                            )}
                            
                            {/* --- NEW SECURITY SHIELD --- */}
                            <button 
                                onClick={() => !isVerified && setOtpModal({...otpModal, show: true, step: 'send'})}
                                className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase border transition-all ${
                                    isVerified 
                                    ? 'bg-green-500/10 text-green-400 border-green-500/30 cursor-default' 
                                    : 'bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700 cursor-pointer'
                                }`}
                                title={isVerified ? "Client Identity Verified" : "Click to Verify Identity"}
                            >
                                {isVerified ? <ShieldCheck size={12} /> : <Shield size={12} />}
                                {isVerified ? 'Verified' : 'Unverified'}
                            </button>

                            <StatusBadge current={status} />
                            
                            <div className="flex items-center gap-2 bg-slate-800 px-2 py-0.5 rounded border border-slate-700">
                                <User size={12} className={owner === 'me' || ownerName !== 'Unassigned Queue' && ownerName !== 'Loading...' && ownerName !== 'Unknown Agent' ? 'text-green-400' : 'text-slate-400'} />
                                <span className="text-xs font-bold text-slate-300">
                                    {ownerName}
                                </span>
                            </div>
                        </div>
                        <p className="text-xs text-slate-500 flex items-center gap-2">
                            <span>#{activeTicket.id}</span> • <span>{activeTicket.subject}</span>
                        </p>
                    </div>
                </div>
                
                {/* ACTION BUTTONS */}
                <div className="flex gap-2">
                    {currentUserRole === 'Manager' ? (
                         <button 
                            onClick={() => setActiveAction('Reassign')}
                            className="px-3 py-1.5 rounded text-xs font-bold bg-slate-800 text-slate-300 border border-slate-700 hover:bg-blue-900/20 hover:text-blue-400 hover:border-blue-500/30 transition-colors flex items-center gap-2"
                        >
                            <ArrowLeftRight size={14} /> Reassign
                        </button>
                    ) : (
                        owner !== 'me' && (
                            <button 
                                onClick={handleClaim}
                                className="px-3 py-1.5 rounded text-xs font-bold bg-blue-600 text-white hover:bg-blue-500 transition-colors flex items-center gap-2 animate-pulse"
                            >
                                <UserCheck size={14} /> Take Over
                            </button>
                        )
                    )}

                    <button 
                        onClick={() => setActiveAction('Hold')}
                        className="px-3 py-1.5 rounded text-xs font-bold bg-slate-800 text-slate-300 border border-slate-700 hover:bg-purple-900/20 hover:text-purple-400 hover:border-purple-500/30 transition-colors flex items-center gap-2"
                    >
                        <Clock size={14} /> Hold
                    </button>
                    <button 
                        onClick={() => setActiveAction('Escalate')}
                        className="px-3 py-1.5 rounded text-xs font-bold bg-slate-800 text-slate-300 border border-slate-700 hover:bg-red-900/20 hover:text-red-400 hover:border-red-500/30 transition-colors flex items-center gap-2"
                    >
                        <ShieldAlert size={14} /> Escalate
                    </button>
                    {status === 'Closed' ? (
                        <button 
                            onClick={handleReopenTicket}
                            className="px-3 py-1.5 rounded text-xs font-bold bg-blue-600 text-white hover:bg-blue-500 transition-colors flex items-center gap-2"
                        >
                            <RefreshCw size={14} /> Reopen
                        </button>
                    ) : (
                        <button 
                            onClick={() => setActiveAction('Close')}
                            className="px-3 py-1.5 rounded text-xs font-bold bg-green-600 text-white hover:bg-green-500 transition-colors flex items-center gap-2"
                        >
                            <CheckCircle size={14} /> Close
                        </button>
                    )}
                </div>
            </div>

            {/* 2. MAIN SPLIT CONTENT */}
            <div className="flex-1 grid grid-cols-2 overflow-hidden">
                <div className="flex flex-col h-full border-r border-slate-800 bg-slate-950/30 overflow-hidden">
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        {messages.filter(msg => {
                            // Filter out internal notes from the main chat view
                            const isLegacyInternalNote = msg.sender === 'auto' && (msg.text.includes('**Ticket Created**') || msg.text.includes('**Internal Notes:**'));
                            return msg.type !== 'internal_note' && !isLegacyInternalNote;
                        }).map((msg) => {
                // 1. ROBUST IDENTITY CHECK
                const senderRaw = msg.sender ? msg.sender.toLowerCase() : '';
                
                // Define who is who
                const isSystem = senderRaw === 'system' || senderRaw === 'auto';
                const isClient = senderRaw === 'client'; 
                // If it's not system and not client, it's staff
                const isStaff = !isSystem && !isClient;

                // 2. DETERMINE ALIGNMENT
                // Standard UI: Client on Left, Staff on Right, System in Center
                const alignmentClass = isSystem ? 'justify-center' : isClient ? 'justify-start' : 'justify-end';

                return (
                    <div key={msg.id} className={`flex ${alignmentClass}`}>
                        {isSystem ? (
                            // --- SYSTEM MESSAGE (CENTER PILL) ---
                            <div className={`text-xs px-3 py-1 rounded-full border flex items-center gap-2 ${msg.text.includes('Verified') ? 'bg-green-900/20 text-green-400 border-green-500/20' : 'bg-slate-900 text-slate-500 border-slate-800'}`}>
                                {msg.text.includes('Verified') && <ShieldCheck size={12}/>}
                                {msg.text}
                            </div>
                        ) : (
                            // --- CHAT BUBBLE (LEFT OR RIGHT) ---
                            <div className={`max-w-[80%] rounded-2xl p-4 shadow-sm ${
                                isClient 
                                    ? 'bg-slate-800 text-slate-200 rounded-tl-none border border-slate-700' // Client Style
                                    : msg.type === 'whatsapp' 
                                        ? 'bg-green-900/40 text-green-100 border border-green-500/20 rounded-tr-none' // WhatsApp Agent
                                        : 'bg-blue-600 text-white rounded-tr-none shadow-blue-900/20' // Standard Agent
                            }`}>
                                {/* SENDER LABEL */}
                                {!isSystem && (
                                    <div className={`flex items-center gap-1.5 mb-2 text-[10px] font-bold uppercase tracking-wider ${isClient ? 'text-slate-500' : 'text-blue-200/70'}`}>
                                        <User size={10} />
                                        {isClient ? (activeTicket.client?.name || 'Client') : (['me', 'agent'].includes(msg.sender) ? currentUserRole : msg.sender || 'Agent')}
                                    </div>
                                )}

                                {/* MESSAGE CONTENT */}
                                <p className="leading-relaxed text-sm whitespace-pre-wrap">{msg.text}</p>
                                
                                {/* TIMESTAMP & READ RECEIPT */}
                                <div className={`text-[10px] mt-2 flex items-center gap-1 opacity-60 ${isClient ? 'justify-start' : 'justify-end'}`}>
                                    <span>{msg.timestamp || 'Just now'}</span>
                                    {!isClient && <CheckCircle size={10} />}
                                </div>
                            </div>
                        )}
                    </div>
                        );
                    })}
                    
                    {/* --- NEW: TYPING INDICATOR UI --- */}
                    {isClientTyping && (
                        <div className="flex justify-start mb-2 animate-fade-in">
                            <div className="bg-slate-800 border border-slate-700 rounded-2xl rounded-tl-none p-4 flex items-center gap-1.5 shadow-sm w-fit">
                                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                            </div>
                        </div>
                    )}

                    {/* --- NEW: INVISIBLE SCROLL ANCHOR --- */}
                    <div ref={messagesEndRef} />
                    </div>

                    {/* INPUT AREA */}
                    <div className="bg-slate-900 border-t border-slate-800 flex-shrink-0">
                        <div className="flex items-center px-4 pt-3 gap-4">
                            <button 
                                onClick={() => setReplyChannel('email')}
                                className={`text-xs font-bold flex items-center gap-2 pb-2 border-b-2 transition-colors ${replyChannel === 'email' ? 'text-blue-400 border-blue-400' : 'text-slate-500 border-transparent hover:text-slate-300'}`}
                            >
                                <Mail size={14} /> Email Reply
                            </button>
                            <button 
                                onClick={() => setReplyChannel('whatsapp')}
                                className={`text-xs font-bold flex items-center gap-2 pb-2 border-b-2 transition-colors ${replyChannel === 'whatsapp' ? 'text-green-400 border-green-400' : 'text-slate-500 border-transparent hover:text-slate-300'}`}
                            >
                                <MessageCircle size={14} /> WhatsApp
                            </button>
                            
                            {/* --- NEW: LIVE CHAT CHANNEL --- */}
                            <button 
                                onClick={() => setReplyChannel('live_chat')}
                                className={`text-xs font-bold flex items-center gap-2 pb-2 border-b-2 transition-colors ${replyChannel === 'live_chat' ? 'text-purple-400 border-purple-400' : 'text-slate-500 border-transparent hover:text-slate-300'}`}
                            >
                                <div className={`w-2 h-2 rounded-full ${replyChannel === 'live_chat' ? 'bg-purple-400 animate-pulse' : 'bg-slate-600'}`}></div>
                                Live Chat
                            </button>
                        </div>

                        <div className="p-4 pt-2">
                            <div className="relative">
                                <textarea 
                                    className={`w-full bg-slate-950 border rounded-xl p-4 pr-12 pb-14 text-white placeholder-slate-500 focus:ring-2 outline-none resize-none h-40 transition-all ${
                                        replyChannel === 'whatsapp' ? 'border-green-900/50 focus:ring-green-500' : 
                                        replyChannel === 'live_chat' ? 'border-purple-900/50 focus:ring-purple-500' : 
                                        'border-slate-700 focus:ring-blue-500'
                                    }`}
                                    placeholder={
                                        replyChannel === 'whatsapp' ? "Type WhatsApp message... (Client will receive instantly)" : 
                                        replyChannel === 'live_chat' ? "Type live message... (Client is currently active)" :
                                        "Draft your email response..."
                                    }
                                    value={inputText}
                                    onChange={(e) => {
                                        setInputText(e.target.value);
                                        // Broadcast our typing status to the client!
                                        if (replyChannel === 'live_chat' && chatChannelRef.current) {
                                            chatChannelRef.current.send({
                                                type: 'broadcast',
                                                event: 'typing',
                                                payload: { sender: 'agent' }
                                            }).catch(() => {}); // Ignore minor connection blips
                                        }
                                    }}
                                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                                />
                                
                                {/* --- LEFT TOOLBAR: TEMPLATES (Email Only) --- */}
                                {replyChannel === 'email' && (
                                    <div className="absolute bottom-3 left-3 z-20">
                                        <button 
                                            onClick={() => setShowTemplates(!showTemplates)}
                                            className="flex items-center gap-2 text-xs font-bold text-slate-400 hover:text-blue-400 bg-slate-900 border border-slate-700 hover:border-blue-500/50 px-3 py-1.5 rounded-lg transition-all"
                                        >
                                            <FileText size={14} /> Insert Template
                                        </button>

                                        {showTemplates && (
                                            <div className="absolute bottom-full left-0 mb-2 w-72 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl overflow-hidden animate-fade-in flex flex-col">
                                                <div className="p-3 border-b border-slate-700 bg-slate-900/50">
                                                    <span className="text-xs font-bold text-slate-400 uppercase">Select Template</span>
                                                </div>
                                                <div className="max-h-48 overflow-y-auto">
                                                    {EMAIL_TEMPLATES.map(t => (
                                                        <button 
                                                            key={t.id}
                                                            onClick={() => {
                                                                const personalized = t.body.replace('{{client_name}}', activeTicket.client?.name || 'Client');
                                                                setInputText(personalized);
                                                                setShowTemplates(false);
                                                            }}
                                                            className="w-full text-left px-4 py-3 text-sm text-slate-300 hover:bg-slate-700 hover:text-white border-b border-slate-700/50 last:border-0 transition-colors"
                                                        >
                                                            {t.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* --- EXPLICIT HUMAN-IN-THE-LOOP SEND BUTTON --- */}
                                <div className="absolute bottom-3 right-3 flex gap-2 z-10">
                                    <button className="p-2 text-slate-400 hover:text-white transition-colors"><Paperclip size={18}/></button>
                                    
                                    <button 
                                        onClick={handleSend} 
                                        disabled={isSending}
                                        className={`px-4 py-2 text-xs font-bold text-white rounded-lg transition-colors shadow-lg flex items-center gap-2 ${isSending ? 'bg-blue-800 cursor-wait' : 'bg-blue-600 hover:bg-blue-500'}`}
                                    >
                                        {isSending ? (
                                            <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div> Sending...</>
                                        ) : (
                                            <><Send size={14}/> {replyChannel === 'email' ? 'Approve & Send Email' : replyChannel === 'live_chat' ? 'Send Live Chat' : 'Send WhatsApp'}</>
                                        )}
                                    </button>
                                    <button 
                                        onClick={handleSend} 
                                        className="p-2 text-white bg-green-600 hover:bg-green-500 rounded-lg transition-colors shadow-lg"
                                    >
                                        <Send size={18}/>
                                    </button>
                                </div>
                            </div>
                            <p className="text-[10px] text-slate-500 mt-2 text-right flex justify-end items-center gap-1">
                                {replyChannel === 'whatsapp' ? <span className="text-green-500 flex items-center gap-1"><Zap size={10}/> Instant Delivery</span> : <span>Standard Email Delivery</span>}
                            </p>
                        </div>
                    </div>
                </div>

                {/* --- RIGHT COLUMN: AI BRAIN --- */}
                <div className="flex flex-col bg-slate-900 h-full overflow-hidden">
                    <div className="h-[40%] border-b border-slate-800 p-6 overflow-y-auto">
                        <h3 className="text-xs font-bold text-purple-400 uppercase mb-4 flex items-center gap-2">
                            <Sparkles size={14} /> AI Suggested Responses
                        </h3>
                        <div className="space-y-3">
                            {isGeneratingDrafts ? (
                                <div className="p-6 bg-slate-800/20 border border-slate-700/30 rounded-xl flex flex-col items-center justify-center text-center animate-pulse">
                                    <Bot size={24} className="text-purple-400 mb-2 animate-bounce" />
                                    <p className="text-xs font-bold text-slate-400">Gemini is analyzing context...</p>
                                    <p className="text-[10px] text-slate-500 mt-1">Drafting personalized responses</p>
                                </div>
                            ) : aiDrafts.length > 0 ? (
                                aiDrafts.map((draft) => (
                                    <div 
                                        key={draft.id} 
                                        onClick={() => setInputText(draft.text)}
                                        className="p-4 bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 hover:border-purple-500/50 rounded-xl cursor-pointer transition-all group"
                                    >
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-[10px] font-bold bg-slate-950 text-slate-400 px-2 py-1 rounded border border-slate-800">{draft.type}</span>
                                            <span className="text-[10px] text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity">Click to Apply</span>
                                        </div>
                                        <p className="text-sm text-slate-300 line-clamp-3">{draft.text}</p>
                                    </div>
                                ))
                            ) : (
                                <div className="p-4 bg-slate-800/20 border border-slate-700/30 rounded-xl text-center">
                                    <p className="text-xs text-slate-500">No AI drafts available.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="h-[60%] flex flex-col bg-slate-950/20">
                        <div className="flex-1 p-6 overflow-y-auto pb-4">
                            <h3 className="text-xs font-bold text-blue-400 uppercase mb-4 flex items-center gap-2">
                                <Bot size={14} /> Live Context & Notes
                            </h3>
                            
                            <div className="mb-6">
                                <div className="flex gap-2 mb-2">
                                    <span className="text-xs text-slate-500">Sentiment:</span>
                                    <span className={`text-xs font-bold ${
                                        activeTicket.sentiment === 'Angry' || activeTicket.sentiment === 'Frustrated' ? 'text-red-400 animate-pulse' : 
                                        activeTicket.sentiment === 'Happy' ? 'text-green-400' : 'text-slate-300'
                                    }`}>
                                        {activeTicket.sentiment || "Analyzing..."}
                                    </span>
                                </div>
                                <div className="flex gap-2">
                                    <span className="text-xs text-slate-500">Detected Intent:</span>
                                    <span className="text-xs font-bold text-white bg-slate-800 px-2 py-0.5 rounded border border-slate-700">
                                        {activeTicket.intent || "Pending"}
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-3 mb-4">
                                {/* Dynamically Fetched Internal Notes */}
                                {messages.filter(msg => msg.type === 'internal_note' || (msg.sender === 'auto' && (msg.text.includes('**Ticket Created**') || msg.text.includes('**Internal Notes:**')))).map((note) => {
                                    const isInitial = note.sender === 'auto';
                                    const bgClass = isInitial ? 'bg-amber-900/10 border-amber-500/20' : 'bg-slate-800/80 border-slate-700';
                                    const textClass = isInitial ? 'text-amber-500' : 'text-blue-400';
                                    
                                    return (
                                        <div key={note.id} className={`flex gap-3 items-start p-3 rounded-lg border shadow-sm ${bgClass}`}>
                                            <FileText size={12} className={`mt-0.5 flex-shrink-0 ${textClass}`} />
                                            <div className="flex-1">
                                                <div className="flex justify-between items-center mb-1">
                                                    <span className={`text-[10px] font-bold uppercase tracking-wider ${textClass}`}>{isInitial ? 'Initial Notes' : note.sender}</span>
                                                    <span className="text-[10px] text-slate-500">{note.timestamp}</span>
                                                </div>
                                                <div 
                                                    className="text-xs text-slate-300 leading-relaxed whitespace-pre-wrap"
                                                    dangerouslySetInnerHTML={{ __html: note.text.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Note Input Box */}
                        <div className="p-6 pt-4 border-t border-slate-800 bg-slate-900/50">
                            <textarea 
                                value={newInternalNote}
                                onChange={(e) => setNewInternalNote(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleAddInternalNote();
                                    }
                                }}
                                placeholder="Type a new internal note..."
                                className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-xs text-slate-300 focus:outline-none focus:border-amber-500/50 resize-none h-20"
                            />
                            <button 
                                onClick={handleAddInternalNote}
                                disabled={!newInternalNote.trim()}
                                className="w-full mt-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-slate-300 text-xs font-bold py-2 rounded-lg transition-colors flex justify-center items-center gap-2"
                            >
                                <FileText size={12} /> Add Case Note
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TicketWorkspace;